import React, {
  useState,
  useEffect,
  useCallback,
  KeyboardEventHandler,
} from "react";
import Axios from "axios";
import { useDropzone } from "react-dropzone";
import { Dialog } from "@fluentui/react";
import {
  DefaultButton,
  DialogFooter,
  DialogType,
  PrimaryButton,
} from "office-ui-fabric-react";
import { Checkbox, TextField } from "@fluentui/react";
import { Get, Delete } from "../../../api/apiUtils";
import { toNominationNo } from "../../../services/Helper";
import SofIcon from "../../common/Icons";
import { AuthProvider } from "../../../AuthProvider";
import ApiSpinner from "../../common/ApiSpinner";
import CreatableSelect from "react-select/creatable";
import "./FileUpload.css";
import { Option } from "../../../models/Option";
import {
  EmailTruckDocumentsRequest,
  EmailRailDocumentsRequest,
  Attachment,
  TruckNomination,
} from "../../../models";
import { emailApi } from "../../../api/emailApi";
import { mergedPdfsAsBytes } from "../../../services/PdfDocumentService";
import moment from "moment";

const formatBytes = (bytes: any, decimals = 2) => {
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  if (bytes == 0) return "0 Byte";
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return (
    Math.round(bytes / Math.pow(1024, i)).toFixed(decimals) + " " + sizes[i]
  );
};

const getBlobUrlFromUrl = async (url: string) => {
  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
  const blob = new Blob([existingPdfBytes], { type: "application/pdf" });
  return URL.createObjectURL(blob);
};

const blobToBase64 = (blob: any) => {
  const reader = new FileReader();
  reader.readAsDataURL(blob);
  return new Promise((resolve) => {
    reader.onloadend = () => {
      resolve(reader.result);
    };
  });
};

const getBase64FromUrl = async (url: string) => {
  const existingPdfBytes = await fetch(url).then((res) => res.arrayBuffer());
  const blob = new Blob([existingPdfBytes], { type: "application/pdf" });
  return (await blobToBase64(blob)) as string;
};

const MAX_SIZE = 5242880; // 5mb
const nameMaxLength = 255; // name length

interface IProps {
  nomination: TruckNomination;
  canUploadFile: boolean;
  modality: number;
}

const FileUpload: React.FC<IProps> = ({
  nomination,
  canUploadFile,
  modality,
}: IProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [files, setFiles] = useState<any[]>([]);
  const [filesProgress, setFilesProgress] = useState<any>({});

  const [showConfirmModal, setShowConfirmModal] = useState<any>({
    show: false,
  });
  const [selectedFile, setSelectedFile] = useState<any>({});
  const [selectedAttachmentId, setSelectedAttachmentId] = useState("");
  const [selectedAttachments, setSelectedAttachments] = useState<any>([]);

  const [request, setRequest] = useState<any>(null);
  const [hasErrorOnDelete, setHasErrorOnDelete] = useState(false);
  const [invalidFileType, setInvalidFileType] = useState(false);
  const [invalidFileSize, setInvalidFileSize] = useState(false);
  const [invalidFileName, setInvalidFileName] = useState(false);
  const [inputValue, setInputValue] = useState<string>("");
  const [emailStatus, setEmailStatus] = useState<string>("default");
  const [emailAddresses, setEmailAddresses] = useState<readonly Option[]>([]);
  const [emailErrorMessage, setEmailErrorMessage] = useState<string>();
  const [currentView, setCurrentView] = useState<string>("default");
  const [selectAllChecked, setSelectAllChecked] = useState<
    boolean | undefined
  >();
  const [keyword, setKeyword] = useState<string>();

  const removeDialogContentProps = {
    type: DialogType.normal,
    title: "Delete Document",
    subText: "Are you sure you want to delete this document?",
  };

  const loadFiles = async () => {
    console.log(nomination);
    setIsLoading(true);
    const response = await Get(`/api/Attachment/${nomination.nominationNo}`);
    const resultRes = await response.json();
    const attachments = await Promise.all(
      (resultRes || []).map(async (file: any) => {
        const blobUrl = await getBlobUrlFromUrl(file.url);
        const base64 = await getBase64FromUrl(file.url);
        return {
          attachmentId: file.attachmentId,
          previewBase64: base64,
          previewBlob: blobUrl,
          url: file.url,
          name: file.fileName,
          size: file.fileSize,
          canDelete: file.canDelete,
          selected: false,
        };
      })
    );
    return attachments;
  };

  useEffect(() => {
    const handleFiles = async () => {
      const data = await loadFiles();
      setFiles(data ?? []);
      setIsLoading(false);
    };
    handleFiles();
  }, []);

  useEffect(() => {
    let emailBody;

    switch (modality) {
      case 1:
        emailBody = `Hello, please find the attached documents for truck # ${nomination?.customerOrderNo}`;
        break;
      case 2:
        emailBody = `Hello, please find the attached documents for Railcar # ${nomination?.railcars}`;
        break;
    }
    setKeyword(emailBody);
  }, []);

  const updateFileProgress = (name: string, value: string | number) => {
    setFilesProgress((prevFiles: any) => ({
      ...prevFiles,
      [name]: value,
    }));
  };

  const nameLengthValidator = (file: any) => {
    if (file.name.length > nameMaxLength) {
      return {
        code: "name-too-large",
        message: `Name is larger than ${nameMaxLength} characters`,
      };
    }
    return null;
  };

  const onDrop = useCallback(async (acceptedFiles, rejectedFiles) => {
    try {
      const token = await AuthProvider.getIdToken();
      const source = Axios.CancelToken.source();
      setRequest(source);
      setIsUploading(true);

      if (rejectedFiles.length > 0) {
        rejectedFiles.map((rejectedFile: any) => {
          if (rejectedFile.errors[0]?.code === "file-invalid-type")
            setInvalidFileType(true);
          if (rejectedFile.errors[0]?.code === "file-too-large")
            setInvalidFileSize(true);
          if (rejectedFile.errors[0]?.code === "name-too-large")
            setInvalidFileName(true);
        });
        setIsUploading(false);
      }

      acceptedFiles.forEach((file: any) => {
        let reader = new FileReader();

        reader.readAsDataURL(file);
        reader.onload = async () => {
          const tempId = Date.now().toString();
          const base64String = reader.result;
          const payload = {
            fileName: file.name,
            content: base64String,
            fileSize: file.size,
          };
          const blob = new Blob([file], { type: "application/pdf" });
          setFiles((prev) => [
            ...prev,
            Object.assign(file, {
              attachmentId: tempId,
              previewBlob: URL.createObjectURL(blob),
              previewBase64: base64String,
              canDelete: true,
              progress: true,
            }),
          ]);
          try {
            updateFileProgress(file.attachmentId, 1);
            const options = {
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
                Authorization: `Bearer ${token.idToken.rawIdToken}`,
              },
              cancelToken: source.token,
              onUploadProgress: (progressEvent: any) => {
                const percentCompleted = Math.round(
                  (progressEvent.loaded * 100) / progressEvent.total
                );
                updateFileProgress(file.attachmentId, percentCompleted);
              },
            };

            const upload = await Axios.post(
              `/api/Attachment/${nomination.nominationNo}`,
              JSON.stringify(payload),
              options
            );

            if (upload.data.attachmentId) {
              setFiles((files) =>
                files.filter((file) => {
                  let updatedValue: any = [];
                  if (file.attachmentId === tempId) {
                    updatedValue = [
                      Object.assign(file, {
                        attachmentId: upload.data.attachmentId,
                        progress: false,
                      }),
                    ];
                  }
                  return [...files, updatedValue];
                })
              );
              updateFileProgress(file.name, 0);
            }
            setIsUploading(false);
            setInvalidFileType(false);
            setInvalidFileSize(false);
            setInvalidFileName(false);
          } catch (error) {
            setFiles((files) =>
              files.filter((file) => {
                let updatedValue: any = [];
                if (file.attachmentId === tempId) {
                  updatedValue = [
                    Object.assign(file, {
                      progress: false,
                      cancelled: true,
                    }),
                  ];
                }
                return [...files, updatedValue];
              })
            );
            setIsUploading(false);
          }
        };
      });
    } catch {
      setIsUploading(false);
    }
  }, []);

  const handleCancelRequest = () => {
    if (request) request.cancel("Request canceled by user");
  };

  const handleDeleteItem = async (item: any) => {
    try {
      setIsUploading(true);
      const response = await Delete(`/api/Attachment/${item.attachmentId}`);
      if (response.status === 200) {
        if (selectedAttachmentId === item.attachmentId) {
          setSelectedFile({});
          setSelectedAttachmentId("");
        }
        setFiles((files) =>
          files.filter((file) => file.attachmentId !== item.attachmentId)
        );
        setShowConfirmModal({ show: false });
        setIsUploading(false);
        setHasErrorOnDelete(false);
      } else {
        setHasErrorOnDelete(true);
      }
    } catch {
      setHasErrorOnDelete(true);
    }
  };

  const printDoc = async () => {
    // const iframe = document.getElementById(
    //   "upload-pdf-viewer"
    // ) as HTMLIFrameElement;
    // if (iframe && iframe.contentWindow) {
    //   iframe.focus();
    //   iframe.contentWindow.print();
    // }
    const documents: any[] = [];

    selectedAttachments.forEach((doc: any) => {
      documents.push(doc.previewBase64);
    });

    const content = await mergedPdfsAsBytes(documents);
    const blob = new Blob([content], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);

    const iframe = document.createElement("iframe") as HTMLIFrameElement;
    document.body.appendChild(iframe);
    iframe.style.display = "none";
    iframe.src = url;

    iframe.onload = function () {
      setTimeout(function () {
        iframe.focus();
        iframe.contentWindow?.print();
      }, 500);
    };
  };

  const downloadDoc = async () => {
    // fetch(url, {
    //   method: "GET",
    //   headers: { "Content-Type": "application/pdf" },
    // })
    //   .then((response) => response.blob())
    //   .then((blob) => {
    //     const url = window.URL.createObjectURL(new Blob([blob]));
    //     const a = document.createElement("a");
    //     document.body.appendChild(a);
    //     a.href = url;
    //     a.download = fileName;
    //     a.click();
    //     document.body.removeChild(a);
    //     URL.revokeObjectURL(url);
    //   });
    const documents: any[] = [];

    selectedAttachments.forEach((doc: any) => {
      documents.push(doc.previewBase64);
    });

    let fileName = selectedAttachments[0].name;

    if (selectedAttachments.length > 1)
      fileName = `PDF_${moment().format("YYYYMMDD_HHmmss")}`;

    const content = await mergedPdfsAsBytes(documents);
    const blob = new Blob([content], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    document.body.appendChild(a);
    a.href = url;
    a.download = fileName;
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const sendEmail = async () => {
    setEmailErrorMessage("");
    if (selectedAttachments.length === 0) return;

    const recipients = emailAddresses.map((a: { value: string }) => a.value);

    if (recipients.length === 0) {
      setEmailErrorMessage("Email Address is required.");
      return;
    }

    let attachments: Attachment[] = [];

    selectedAttachments.forEach((file: any) => {
      const payload = {
        fileName: file.name,
        content: file.previewBase64,
        mimeType: "application/pdf",
      } as Attachment;

      attachments.push(payload);
    });

    setEmailStatus("sending");

    let resp;

    switch (modality) {
      case 1:
        const truckPayload = {
          recipients: recipients,
          attachments: attachments,
          emailBody: keyword,
          customerOrderNo: nomination?.customerOrderNo,
        } as EmailTruckDocumentsRequest;

        resp = await emailApi.sendEmailTruckDocumentsAsync(truckPayload);
        break;
      case 2:
        const railPayload = {
          recipients: recipients,
          attachments: attachments,
          emailBody: keyword,
          railCarNumber: nomination?.railcars,
        } as EmailRailDocumentsRequest;

        resp = await emailApi.sendEmailRailDocumentsAsync(railPayload);
        break;
    }

    if (resp.emailStatus === true) {
      setEmailStatus("success");
      setCurrentView("emailsent");
    } else {
      setEmailStatus("error");
    }

    setTimeout(() => {
      setEmailAddresses([]);
      setEmailStatus("default");
      setCurrentView("default");
    }, 3000);
  };

  const handleDeleteFunc = (
    e: React.MouseEvent<HTMLElement>,
    type?: string,
    data?: any
  ) => {
    e.preventDefault();
    e.stopPropagation();
    if (type === "delete") {
      setShowConfirmModal({ show: true, file: data });
      setFilesProgress({});
    }
    if (type === "cancelrequest") handleCancelRequest();
    if (type === "state") {
      setFiles((files) =>
        files.filter((f) => f.attachmentId !== data.attachmentId)
      );
      setFilesProgress({});
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "application/pdf": [],
      // 'image/*': [], // images
      // 'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [], // docs
    },
    multiple: true,
    maxSize: MAX_SIZE,
    onDrop,
    disabled: isUploading || isLoading,
    validator: nameLengthValidator,
  });

  const sortFiles = (files: any[]) => {
    return [...files].sort((a: any, b: any) => {
      if (filesProgress[a.attachmentId] && filesProgress[a.attachmentId] !== 0)
        return -1;
      if (filesProgress[b.attachmentId] && filesProgress[b.attachmentId] !== 0)
        return 1;

      return (a?.name || "").localeCompare(b?.name || "");
    });
  };

  const components = {
    DropdownIndicator: null,
  };

  const createOption = (label: string) => ({
    label,
    value: label,
  });

  const validateEmail = (email: string) => {
    return String(email)
      .toLowerCase()
      .match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|.(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
  };

  const handleKeyDown: KeyboardEventHandler = (event) => {
    if (!inputValue) return;
    switch (event.key) {
      case "Enter":
      case "Tab":
      case " ":
      case ";":
        if (!validateEmail(inputValue)) {
          setEmailErrorMessage("Invalid email address.");
          return;
        }
        if (emailAddresses === null) {
          const obj: readonly Option[] = [
            { value: inputValue, label: inputValue },
          ];
          setEmailAddresses(obj);
        } else {
          setEmailAddresses([...emailAddresses, createOption(inputValue)]);
        }

        setInputValue("");
        setEmailErrorMessage("");
        event.preventDefault();
    }
  };

  const showEmailPanel = () => {
    setCurrentView("email");
  };

  const showDefaultPanel = () => {
    setCurrentView("default");
  };

  const handleSelectedAttachement = (value?: boolean, data?: any) => {
    if (value) {
      const newFiles = [...files];

      const index = newFiles.findIndex(
        (s) => s.attachmentId === data.attachmentId
      );

      newFiles[index].selected = true;

      setFiles(newFiles);

      setSelectedAttachments([...selectedAttachments, data]);
    } else {
      const newFiles = [...files];

      const index = newFiles.findIndex(
        (s) => s.attachmentId === data.attachmentId
      );

      newFiles[index].selected = false;

      setFiles(newFiles);

      setSelectedAttachments((a: any) =>
        a.filter((f: any) => f.attachmentId !== data.attachmentId)
      );
    }
  };

  const handleDeleteSelectedAttachment = (
    e: React.FormEvent<HTMLElement>,
    data?: any
  ) => {
    const newFiles = [...files];

    const index = newFiles.findIndex(
      (s) => s.attachmentId === data.attachmentId
    );

    newFiles[index].selected = false;

    setFiles(newFiles);

    setSelectedAttachments((a: any) =>
      a.filter((f: any) => f.attachmentId !== data.attachmentId)
    );
  };

  const handleSelectAllAttachments = (value?: boolean) => {
    const newFiles = [...files];
    setSelectAllChecked(value);
    newFiles.map((file, index) => {
      const data = (file.selected = value);

      return data;
    });

    setFiles(newFiles);
    setSelectedAttachments(newFiles.filter((s) => s.selected === true));
  };

  return (
    <>
      <div className="div-container">
        <div className="div-preview">
          {selectedFile[selectedAttachmentId]?.name ? (
            <div className="preview-filename">
              <div className="file-details">
                <div className="file-img">
                  <SofIcon iconname="PDFFile" />
                </div>
                <div className="file-name">
                  <h3>
                    {selectedFile[selectedAttachmentId]?.name
                      .split(".")
                      .slice(0, -1)
                      .join(".")}
                  </h3>
                </div>
                          </div>
                          <DefaultButton
                              onClick={() => {
                                  setSelectedFile({});
                              }}
                              disabled={isLoading}
                              text="Close"
                          />
              {/*<button*/}
              {/*  onClick={() => {*/}
              {/*    setSelectedFile({});*/}
              {/*  }}*/}
              {/*>*/}
              {/*  Close*/}
              {/*</button>*/}
            </div>
          ) : null}
          {selectedFile[selectedAttachmentId]?.name ? (
            <iframe
              id="upload-pdf-viewer"
              title="PDF Viewer"
              src={`${selectedFile[selectedAttachmentId]?.url}#toolbar=0&zoom=100`}
              width="100%"
              height="100%"
            />
          ) : (
            <div className="empty-preview">
              <SofIcon iconname="DocumentSearch" />
              <span>Open file to preview</span>
            </div>
          )}
        </div>
        {currentView === "default" && (
          <>
            <div className="div-upload">
              <div className="upload-details">
                {/* <h1>File Uploads</h1> */}
                <div className="vessel-type flex">
                  {/*<div className={`icon truck-icon`}></div>*/}
                  <div className="details">
                    <h3>{nomination.tank}</h3>
                    <span>#{`${toNominationNo(nomination.nominationNo)}`}</span>
                  </div>
                </div>
                <div className="upload-actions">
                  <div className="upload-actions-checkbox">
                    {files.length > 0 && (
                      <Checkbox
                        label="Select All"
                        checked={selectAllChecked}
                        onChange={(e, value?: boolean) =>
                          handleSelectAllAttachments(value)
                        }
                      />
                    )}

                    {!isLoading && invalidFileType && (
                      <p className="error-txt">
                        File type must be application/pdf
                      </p>
                    )}
                    {!isLoading && invalidFileSize && (
                      <p className="error-txt">File is larger than 5MB</p>
                    )}
                    {!isLoading && hasErrorOnDelete && (
                      <p className="error-txt">
                        Error on deleting the document
                      </p>
                    )}
                    {!isLoading && invalidFileName && (
                      <p className="error-txt">
                        Name is larger than {nameMaxLength} characters
                      </p>
                    )}
                  </div>

                  <div className="upload-actions-ctas">
                    <span onClick={showEmailPanel}>
                      <div className="div-button-icon">
                        <div>Email</div>
                        <div>
                          <SofIcon iconname="Mail" />
                        </div>
                      </div>
                    </span>
                    <span
                      className={`upload-download ${
                        selectedAttachments.length > 0 ? "" : "disabled"
                      }`}
                      onClick={() =>
                        selectedAttachments.length > 0 ? downloadDoc() : null
                      }
                    >
                      <div className="div-button-icon">
                        <div>Download</div>
                        <div>
                          <SofIcon iconname="Download" />
                        </div>
                      </div>
                    </span>
                    <span
                      className={`upload-print ${
                        selectedAttachments.length > 0 ? "" : "disabled"
                      }`}
                      onClick={() =>
                        selectedAttachments.length > 0 ? printDoc() : null
                      }
                    >
                      <div className="div-button-icon">
                        <div>Print</div>
                        <div>
                          <SofIcon iconname="Print" />
                        </div>
                      </div>
                    </span>
                  </div>
                </div>
                <div className="file-list-wrapper">
                  <ul className="file-list">
                    {isLoading && <ApiSpinner />}

                    {!isLoading && files.length === 0 && (
                      <p style={{ fontWeight: "bold" }}>No documents found</p>
                    )}

                    {sortFiles(files || []).map((file: any, i: number) => {
                      return (
                        <li
                          aria-disabled={isUploading}
                          className={`${
                            selectedFile[file.attachmentId]?.active
                              ? "active"
                              : ""
                          } ${
                            file?.progress || file?.cancelled
                              ? "progressitem"
                              : ""
                          }`}
                          onClick={async (e) => {
                            if (
                              file.attachmentId ===
                                selectedFile[selectedAttachmentId]?.id ||
                              file?.cancelled
                            ) {
                              setSelectedFile({});
                            } else {
                              setSelectedFile({
                                [file.attachmentId]: {
                                  id: file.attachmentId,
                                  file:
                                    typeof file?.progress == "boolean"
                                      ? file.previewBlob
                                      : file.previewBase64,
                                  name: file.name,
                                  active: true,
                                  url: file.previewBlob,
                                  base64: file.previewBase64,
                                },
                              });
                            }
                            setSelectedAttachmentId(file.attachmentId);
                          }}
                          key={i}
                        >
                          <div className="file-details">
                            <div className="file-img">
                              {file?.name?.split(".").pop() === "pdf" && (
                                <SofIcon iconname="PDFFile" />
                              )}
                              {/* {file?.type.includes('image') && <SofIcon iconname="ImageFile" />}
                          {file?.type.includes('application/vnd') && <SofIcon iconname="WordFile" />} */}
                            </div>
                            <div className="file-name">
                              <h3>
                                {file?.name.split(".").slice(0, -1).join(".")}
                              </h3>
                              <span>
                                {file?.progress ? (
                                  <>
                                    {filesProgress[file.attachmentId] ?? 0}%
                                    Done
                                  </>
                                ) : (
                                  formatBytes(file.size)
                                )}
                              </span>
                            </div>
                          </div>
                          <div className="file-cta">
                            <span>
                              <Checkbox
                                checked={file.selected}
                                onChange={(e, value?: boolean) =>
                                  //console.log(value)
                                  handleSelectedAttachement(value, file)
                                }
                              />
                            </span>
                            {file.canDelete &&
                              !file?.progress &&
                              !file?.cancelled && (
                                <span
                                  aria-disabled={isUploading}
                                  onClick={(
                                    e: React.MouseEvent<HTMLElement>
                                  ) => {
                                    handleDeleteFunc(e, "delete", file);
                                  }}
                                >
                                  <SofIcon iconname="DeleteItem" />
                                </span>
                              )}
                            {file?.progress && !file?.cancelled && (
                              <span
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  handleDeleteFunc(e, "cancelrequest");
                                }}
                              >
                                <SofIcon iconname="CloseIcon" />
                              </span>
                            )}
                            {file?.cancelled && (
                              <span
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  handleDeleteFunc(e, "state", {
                                    attachmentId: file.attachmentId,
                                  });
                                }}
                              >
                                <SofIcon iconname="CloseIcon" />
                              </span>
                            )}
                            {file?.progress &&
                            !file?.cancelled &&
                            filesProgress[file.attachmentId] === 100 ? (
                              <span className="completed">Completed</span>
                            ) : file?.cancelled ? (
                              <span className="completed cancelled">
                                Cancelled
                              </span>
                            ) : !file?.progress ? (
                              ""
                            ) : (
                              <span>{formatBytes(file.size)}</span>
                            )}
                          </div>

                          {file?.progress && (
                            <div className="progressbarWrapper">
                              <span
                                style={{
                                  width: `${filesProgress[file.attachmentId]}%`,
                                }}
                                className="upload-progressbar"
                              ></span>
                            </div>
                          )}
                          {file?.cancelled && (
                            <div className="progressbarWrapper">
                              <span
                                style={{
                                  width: `${filesProgress[file.attachmentId]}%`,
                                }}
                                className="upload-progressbar cancelled"
                              ></span>
                            </div>
                          )}
                        </li>
                      );
                    })}
                  </ul>
                </div>
              </div>

              <div className="upload">
                {canUploadFile && (
                  <div
                    {...getRootProps({
                      className: "dropzone",
                    })}
                  >
                    <input {...getInputProps()} />
                    <div className="upload-file-text">
                      <SofIcon iconname="Upload" />
                      {isDragActive ? (
                        <p>Drop files here to upload</p>
                      ) : (
                        <p>
                          Drop files here to upload or <br />
                          <span>Choose File</span>
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </>
        )}
        {currentView === "email" && (
          <>
            <div className="div-upload">
              <div className="div-details">
                <div className="vessel-type flex">
                  {/*<div className={`icon truck-icon`}></div>*/}
                  <div className="details">
                    <h3>{nomination.tank}</h3>
                    <span>#{`${toNominationNo(nomination.nominationNo)}`}</span>
                  </div>
                </div>
                <div style={{ color: "red", padding: 20 }}>
                  {selectedAttachments.length === 0 && (
                    <>Please select file attachment(s)</>
                  )}
                </div>
                <div>
                  <div style={{ padding: 10 }}>
                    <div style={{ padding: 10 }}>
                      <span>Recipients</span>
                    </div>

                    <div style={{ padding: 10 }}>
                      <CreatableSelect
                        components={components}
                        inputValue={inputValue}
                        isClearable
                        isMulti
                        menuIsOpen={false}
                        onChange={(email: any) => setEmailAddresses(email)}
                        onInputChange={(email) => setInputValue(email)}
                        onKeyDown={handleKeyDown}
                        placeholder="Enter email address..."
                        value={emailAddresses}
                        isDisabled={emailStatus === "sending" ? true : false}
                      />
                      <div style={{ color: "red", fontSize: 10 }}>
                        {emailErrorMessage}
                      </div>
                    </div>

                    <div style={{ padding: 10 }}>
                      <span>Message</span>
                    </div>
                    <div style={{ padding: 10 }}>
                      <TextField
                        multiline={true}
                        height={300}
                        onChange={(e, newValue?: string | undefined) =>
                          setKeyword(newValue === undefined ? "" : newValue)
                        }
                        maxLength={1000}
                        value={keyword}
                        rows={8}
                        disabled={emailStatus === "sending" ? true : false}
                      />
                    </div>
                    <div style={{ padding: 10 }}>
                      <span>Attachments</span>
                    </div>
                  </div>
                </div>
                <div className="file-attachment-list-wrapper">
                  <ul className="file-list">
                    {!isLoading && selectedAttachments.length === 0 && (
                      <p style={{ fontWeight: "bold" }}>No documents found</p>
                    )}

                    {sortFiles(selectedAttachments || []).map(
                      (file: any, i: number) => {
                        return (
                          <li
                            aria-disabled={isUploading}
                            className={`${
                              selectedFile[file.attachmentId]?.active
                                ? "active"
                                : ""
                            } ${
                              file?.progress || file?.cancelled
                                ? "progressitem"
                                : ""
                            }`}
                            onClick={async (e) => {
                              if (
                                file.attachmentId ===
                                  selectedFile[selectedAttachmentId]?.id ||
                                file?.cancelled
                              ) {
                                setSelectedFile({});
                              } else {
                                setSelectedFile({
                                  [file.attachmentId]: {
                                    id: file.attachmentId,
                                    file:
                                      typeof file?.progress == "boolean"
                                        ? file.previewBlob
                                        : file.previewBase64,
                                    name: file.name,
                                    active: true,
                                    url: file.previewBlob,
                                    base64: file.previewBase64,
                                  },
                                });
                              }
                              setSelectedAttachmentId(file.attachmentId);
                            }}
                            key={i}
                          >
                            <div className="file-details">
                              <div className="file-img">
                                {file?.name?.split(".").pop() === "pdf" && (
                                  <SofIcon iconname="PDFFile" />
                                )}
                                {/* {file?.type.includes('image') && <SofIcon iconname="ImageFile" />}
                          {file?.type.includes('application/vnd') && <SofIcon iconname="WordFile" />} */}
                              </div>
                              <div className="file-name">
                                <h3>
                                  {file.name.split(".").slice(0, -1).join(".")}
                                </h3>
                                <span>
                                  {file?.progress ? (
                                    <>
                                      {filesProgress[file.attachmentId] ?? 0}%
                                      Done
                                    </>
                                  ) : (
                                    formatBytes(file.size)
                                  )}
                                </span>
                              </div>
                            </div>
                            <div className="file-cta">
                              <span
                                aria-disabled={isUploading}
                                onClick={(e: React.MouseEvent<HTMLElement>) => {
                                  handleDeleteSelectedAttachment(e, file);
                                }}
                              >
                                <SofIcon iconname="DeleteItem" />
                              </span>
                            </div>
                          </li>
                        );
                      }
                    )}
                  </ul>
                </div>
              </div>
              <div className="upload">
                <div
                  style={{
                    display: "flex",
                    gap: "0.5rem",
                    justifyContent: "flex-end",
                  }}
                >
                  <div>
                    <button
                      type="submit"
                      onClick={sendEmail}
                      className="send-button"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em",
                        }}
                      >
                        <div style={{ position: "relative", top: "-1px" }}>
                          {emailStatus === "default" && (
                            <svg
                              width="24"
                              height="24"
                              fill="none"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="m12.815 12.197-7.532 1.256a.5.5 0 0 0-.386.318L2.3 20.728c-.248.64.421 1.25 1.035.943l18-9a.75.75 0 0 0 0-1.342l-18-9c-.614-.307-1.283.304-1.035.943l2.598 6.957a.5.5 0 0 0 .386.319l7.532 1.255a.2.2 0 0 1 0 .394Z"
                                fill="#fff"
                              />
                            </svg>
                          )}
                          {emailStatus === "sending" && (
                            <svg
                              width="24"
                              height="24"
                              fill="none"
                              viewBox="0 0 24 24"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M12 4.5a7.5 7.5 0 1 0 7.419 6.392c-.067-.454.265-.892.724-.892.37 0 .696.256.752.623A9 9 0 1 1 18 5.292V4.25a.75.75 0 0 1 1.5 0v3a.75.75 0 0 1-.75.75h-3a.75.75 0 0 1 0-1.5h1.35a7.474 7.474 0 0 0-5.1-2Z"
                                fill="#fff"
                              />
                            </svg>
                          )}
                        </div>
                        {emailStatus === "default" && <div>Send</div>}
                        {emailStatus === "sending" && <div>Sending</div>}
                      </div>
                    </button>
                  </div>
                  <div>
                    <button
                      type="submit"
                      onClick={showDefaultPanel}
                      className="cancel-button"
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5em",
                        }}
                      >
                        <div
                          style={{ position: "relative", top: "-1px" }}
                        ></div>
                        <div>Cancel</div>
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
        {currentView === "emailsent" && (
          <div className="div-upload">
            <div className="upload-details">
              <div className="vessel-type flex">
                {/*<div className={`icon truck-icon`}></div>*/}
                <div className="details">
                  <h3>{nomination.tank}</h3>
                  <span>#{`${toNominationNo(nomination.nominationNo)}`}</span>
                </div>
              </div>
              <div className="email-sent">
                <img src="/rafiki.png" alt="email sent" />
                <h2>Email Sent!</h2>
                <p>Your email has been successfully sent.</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <Dialog
        hidden={!showConfirmModal.show}
        dialogContentProps={removeDialogContentProps}
        styles={{ main: { width: 350 } }}
        onDismiss={() => setShowConfirmModal({ show: false })}
      >
        <DialogFooter>
          <PrimaryButton
            onClick={() => handleDeleteItem(showConfirmModal?.file)}
            text="Confirm"
          />
          <DefaultButton
            onClick={() => setShowConfirmModal({ show: false })}
            text="Cancel"
          />
        </DialogFooter>
      </Dialog>
    </>
  );
};

export default FileUpload;
